import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/core/utils/utils";
import { ChangeStepper } from "./change-stepper";
import { ARTIFACT_CONTROLS, PANE_BOTTOM_CLEARANCE } from "./constants";
import { DiffTotals } from "./diff-totals";
import { PaneSearchControl } from "./pane-search-control";
import { passageText, rulesDocumentDiff } from "./rules-diff";
import { RulesPassage } from "./rules-passage";
import { RulesView } from "./rules-view";
import type { Workspace } from "./types";
import { usePaneSearch } from "./use-pane-search";

/**
 * The written pay plan with the proposal's edits marked in place.
 *
 * Passages are not lifted out of the document. A rule only means something
 * beside the rules around it, so the whole plan is here and the changes are
 * coloured where they sit — with the words that actually moved marked inside a
 * reworded paragraph, because colouring the whole paragraph only repeats what
 * the reader already knew.
 */
export const RulesChanges = ({
  workspace,
  diff,
}: {
  workspace: Workspace;
  /** Computed by the tab strip, which needs the count for its own mark. */
  diff: ReturnType<typeof rulesDocumentDiff>;
}) => {
  const { blocks, changes, comparable, added, removed } = diff;
  const document_ = useRef<HTMLDivElement>(null);
  // A passage is the unit, the way a row is on the roster and a cell is on the
  // grid. Searching the lines it was built from rather than the rendered text
  // keeps the markdown sentinels the diff puts in out of the haystack.
  const search = usePaneSearch(
    useMemo(
      () =>
        blocks.map((block, index) => ({
          id: String(index),
          text: passageText(block.lines),
        })),
      [blocks],
    ),
    document_,
  );
  // The same normalisation the hook matches on, so what is marked and what is
  // counted can never disagree.
  const needle = search.open ? search.query.trim().toLowerCase() : "";
  useEffect(() => {
    if (!search.activeId) return;
    const node = document_.current?.querySelector(
      `[data-passage="${search.activeId}"]`,
    );
    const viewport = document_.current?.closest<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!node || !viewport) return;
    viewport.scrollTop +=
      node.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      viewport.clientHeight / 2;
  }, [search.activeId]);
  const [step, setStep] = useState(0);
  // Derived, not stored. The assistant can rewrite the plan under the reader
  // and leave fewer edits than before, and an index kept in state would then
  // point past the end of the document it is meant to be walking.
  const at = changes ? ((step % changes) + changes) % changes : 0;

  const goTo = (by: number) => {
    if (!changes) return;
    const next = (((at + by) % changes) + changes) % changes;
    setStep(next);
    const node = document_.current?.querySelector(`[data-change="${next}"]`);
    const viewport = document_.current?.closest<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!node || !viewport) return;
    // Scroll the one container, by hand. `scrollIntoView` walks every
    // scrollable ancestor, so stepping through edits in this pane also moved
    // the page and the transcript beside it.
    viewport.scrollTop +=
      node.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      viewport.clientHeight / 2;
  };

  if (!workspace.proposed)
    return (
      <section className="space-y-2 p-4">
        <h2 className="font-semibold">What changed</h2>
        <p className="text-secondary-text">
          Nothing has been proposed yet. Describe a change and the wording you
          see here will update.
        </p>
      </section>
    );

  if (!comparable)
    return (
      <section className="space-y-2 p-4">
        <h2 className="font-semibold">What changed</h2>
        <p className="text-secondary-text">
          Your written rules are too long to mark individual changes. Read both
          versions below.
        </p>
        <RulesView
          title="Current rules"
          rules={workspace.current}
          empty="No current written rules."
        />
        <RulesView
          title="Proposed rules"
          rules={workspace.proposed}
          empty="No proposed written rules."
        />
      </section>
    );

  // A business whose written rules are empty. The document is what this view
  // renders, so with nothing in it the pane came out blank below its own
  // header, which reads as a page that failed to load rather than as a plan
  // nobody has written yet.
  if (!blocks.some((block) => block.lines.some((line) => line.trim())))
    return (
      <section className="space-y-2 p-4">
        <h2 className="font-semibold">What changed</h2>
        <p className="text-secondary-text">
          Your rules have not been written down yet. Describe how pay works and
          the wording will appear here beside the calculation.
        </p>
      </section>
    );

  const section = blocks.find((block) => block.change === at)?.section;

  return (
    <section className="flex min-h-0 flex-col">
      {/* The count, where you are in it, and the way through: one line, and it
          stays put while the document scrolls under it.

          With nothing to step through this used to be a dead end: a page that
          said the wording had not moved and to go ask the assistant. The rules
          themselves are what the reader wanted, and an unchanged diff already
          renders them, so the document stays and only the header changes. That
          is also what retires the separate Current and Proposed tabs — one view
          reads as the rules when nothing moved and as a diff when something
          did. */}
      <div
        className={cn(
          ARTIFACT_CONTROLS,
          "sticky top-0 z-10 flex flex-wrap items-center gap-x-3 gap-y-1",
        )}
      >
        {!changes ? (
          <span className="text-secondary-text">
            {workspace.has_changes
              ? "Your written rules read the same as today. The proposal changes how pay is calculated."
              : "Your written rules"}
          </span>
        ) : (
          <>
            {/* The stepper leads the row. The sentence it replaced said how
                many edits there are, which is the number the counter already
                carries. */}
            <ChangeStepper
              noun="edit"
              label={`Edit ${at + 1} of ${changes}`}
              onStep={goTo}
            />
            {!!section && (
              <span className="min-w-0 truncate text-tertiary-text">
                {section}
              </span>
            )}
            {/* With the stepper and the section, not out by the search. Both
                numbers answer "how much of this moved", so they read together;
                the search is a tool and belongs where every other pane in the
                editor keeps it. It used to carry an `ml-auto` of its own, and
                with the search carrying one too it came to rest in the middle
                of the row, attached to neither side. */}
            <DiffTotals added={added} removed={removed} className="shrink-0" />
          </>
        )}
        <PaneSearchControl search={search} label="rules" className="ml-auto" />
      </div>
      <div
        ref={document_}
        className={cn("min-w-0 pt-2", PANE_BOTTOM_CLEARANCE)}
      >
        {blocks.map((block, index) => (
          <RulesPassage
            key={index}
            block={block}
            at={index}
            active={!!changes && block.change === at}
            needle={needle}
            hit={search.activeId === String(index)}
          />
        ))}
      </div>
    </section>
  );
};
